import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalTitle } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import EmptyData from "go-core/components/EmptyData";
import { LoadingEmptyData } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import InputWithSearch from "../../../../components/Common/Inputs/InputWithSearch";
import { partnerApi } from "../../../../services/Api/Partner/partnerApi";
import { CreateLoanApi, OrganizationApi } from "../../../../services/Api/types";
import CreatePartnerOrganizationModal from "../../pages/Partner/pages/Preview/components/CreatePartnerOrganizationModal";

interface Props {
	partnerId: number;
	onHide: (data?: OrganizationApi) => Promise<void>;
}

export interface OrganizationSearchOption {
	name: string;
	id: number;
}

const CreateLoanModal = ({ partnerId, onHide }: Props) => {
	const history = useHistory();
	const { t } = useTranslation("admin");
	const form = useForm<CreateLoanApi>({
		defaultValues: {
			partner_id: partnerId,
		},
	});
	const { formState, setValue } = form;
	const [searchValue, setSearchValue] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [organizations, setOrganizations] = useState<OrganizationSearchOption[]>([]);
	const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);

	useEffect(() => {
		onSearchOrganizationHandler();
	}, [searchValue]);

	const onSearchOrganizationHandler = async () => {
		setLoading(true);
		const res = await partnerApi.getPartnerOrganizationsSearchSelect(searchValue, {}, {}, partnerId);
		setOrganizations([
			...res.map((data) => {
				return {
					name: data.label,
					id: Number(data.id),
				};
			}),
		]);
		setLoading(false);
	};

	const onAddOrganizationOptionHandler = (item: OrganizationSearchOption) => {
		setValue("organization_id", item.id);
		history.push(`/partnership/${partnerId}/loans/new?organization_id=${item.id}`);
	};

	const onCreateHandler = () => {
		setShowCreateOrganizationModal(true);
	};

	return (
		<>
			{showCreateOrganizationModal && (
				<CreatePartnerOrganizationModal
					partnerId={partnerId}
					searchValue={searchValue}
					onHide={() => setShowCreateOrganizationModal(false)}
					onSelectOrganization={(org: OrganizationSearchOption) => onAddOrganizationOptionHandler(org)}
				/>
			)}
			<Modal show onHide={onHide}>
				<FormDirty formState={formState} key="invoice-details-form">
					<Modal.Header closeButton>
						<ModalTitle>{t("modules.loan.field.create_loan.title")}</ModalTitle>
					</Modal.Header>
					<ModalBody className="items">
						<div className="sticky-modal-search-header">
							<div className="form-group search-input">
								<div className="input-group">
									<InputWithSearch
										onButtonClick={onCreateHandler}
										onInput={(value: string) => setSearchValue(value)}
									/>
								</div>
							</div>
						</div>
						<table className="table">
							<tbody>
								{loading ? (
									<LoadingEmptyData />
								) : (
									<>
										{organizations.length > 0 ? (
											organizations.map((organization, index) => (
												<tr key={index}>
													<td>
														<Link
															to={`/partnership/${partnerId}/organizations/${organization.id}`}
															target={"_blank"}
															rel="noreferrer"
														>
															{organization.name}
														</Link>
													</td>
													<td className="px-0">
														<Button
															className="float-end"
															variant={"primary-light"}
															type="button"
															onClick={() => onAddOrganizationOptionHandler(organization)}
														>
															{t("common.action.select", { ns: "lib" })}
														</Button>
													</td>
												</tr>
											))
										) : (
											<EmptyData />
										)}
									</>
								)}
							</tbody>
						</table>
					</ModalBody>
				</FormDirty>
			</Modal>
		</>
	);
};
export default CreateLoanModal;
